// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function WhiteNoiceCalmSleepTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="White Noice: Calm Sleep"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “White
        Noice: Calm Sleep” (the “App”). By downloading, installing, accessing,
        or using the App, you agree to be bound by these Terms. If you do not
        agree, you must uninstall the App and discontinue all use. Where
        required by platform rules or local law, additional terms may apply; in
        case of conflict with App Store or Google Play terms, the platform terms
        may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App (Non-Medical)</div>
      <p>
        The App provides relaxing soundscapes such as white noise and nature
        sounds, along with optional sleep timers and mixing features, to support
        calm, relaxation, and focus. It is intended for general well-being,
        comfort, and ambient sound only. The App is not a medical device, does
        not diagnose or treat sleep disorders or any other condition, and does
        not replace professional advice from doctors, pediatricians, or other
        qualified specialists. Parents and caregivers remain solely responsible
        for decisions about infant and child sleep routines and should follow
        medical guidance and safe-sleep recommendations applicable in their
        country.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Audio Use and Safety</div>
      <p>
        You are responsible for how you use sound playback, including volume
        levels, duration, and device placement. Prolonged listening at high
        volumes may damage hearing, including for infants and young children.
        When using the App around babies or children, you should follow
        pediatric guidance on safe volumes and distances and ensure that the
        device, cables, or speakers are placed safely out of reach.
      </p>
      <p>
        You must also ensure that ambient sounds used for sleep do not prevent
        you from hearing important alerts or signals (for example, alarms,
        crying, or safety warnings). The App does not monitor your environment
        and does not provide emergency alerts.
      </p>

      <div className={s.title}>5) Free and Premium Features</div>
      <p>
        The App may include a free tier and a premium tier. Free access typically
        includes a limited set of sounds (for example, a white noise sound or a
        small number of base loops) and may provide basic playback controls or
        limited timer presets. Premium access may unlock additional nature
        sounds, extended or custom sleep timers, mixing of multiple sounds, and
        other advanced features. The exact feature set for each tier may change
        over time and may be described within the App or on the paywall screen.
      </p>

      <div className={s.title}>6) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features may be offered through auto-renewable subscriptions or
        other in-app purchases (“Subscriptions”). Typical plans may include
        monthly and/or annual options, and may offer an introductory free trial
        period. Exact pricing, trial length, and plan names displayed in the
        store can vary by region and over time, and are determined by the
        platform and configuration used (for example, via RevenueCat and store
        products).
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are handled according
        to the policies of the relevant app store.
      </p>

      <div className={s.title}>7) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        sounds, extended timers, mixing features, and other paid functionality.
        An internet connection may be required to validate or refresh
        entitlements after long offline periods. If platform billing reverses,
        refunds, or flags a transaction as fraudulent, related entitlements may
        be limited, suspended, or revoked.
      </p>

      <div className={s.title}>8) Playback, Background Use, and Timers</div>
      <p>
        The App is designed to continue playing audio in the background and when
        the device is locked, subject to device settings, system limitations,
        and your choices. Sleep timers, where available, may attempt to fade out
        audio and stop playback after a selected period. However, we do not
        guarantee that timers will always operate perfectly in all conditions
        (for example, due to OS restrictions, battery saver modes, or force
        quits). You remain responsible for verifying that playback and timers
        are behaving as you intend and for manually stopping playback when
        necessary.
      </p>

      <div className={s.title}>9) Downloads, Caching, and Offline Use</div>
      <p>
        Some audio content may be bundled with the App, while additional sounds
        may be downloaded from remote storage and cached locally on your device.
        Cached files may be stored up to a defined size limit and removed
        automatically to free space. Previously cached sounds may continue to be
        available offline; new downloads generally require an internet
        connection. We do not guarantee that particular sounds or packs will
        always remain available for download, and content may be updated,
        replaced, or retired over time.
      </p>

      <div className={s.title}>10) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, personal, non-commercial
        purposes and not to:
      </p>
      <ul>
        <li>
          use the App or its audio content in a way that violates laws or
          regulations, including noise, privacy, or nuisance regulations;
        </li>
        <li>
          attempt to extract, copy, or redistribute bundled or streamed audio
          files outside the normal functionality of the App;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          use automated tools to scrape, copy, or systematically download
          content beyond normal personal use;
        </li>
        <li>
          use the App in any way that interferes with or disrupts servers,
          networks, or third-party services used by the App.
        </li>
      </ul>

      <div className={s.title}>11) Intellectual Property</div>
      <p>
        All sounds, audio loops, graphics, images, UI elements, logos, text, and
        other materials provided with the App (excluding any content you
        separately own or supply) are owned by the publisher or its licensors
        and are protected by intellectual property laws. Subject to these Terms
        and any Subscription limitations, you are granted a limited,
        non-exclusive, non-transferable, revocable license to use the App and
        its content solely for personal relaxation, sleep, or focus. You may not
        resell, redistribute, or offer the audio content as a stand-alone
        product or service, and you may not remove or alter any proprietary
        notices.
      </p>

      <div className={s.title}>12) Third-Party Services and Infrastructure</div>
      <p>
        The App may use third-party services for audio playback, background
        control, storage, caching, analytics, subscriptions, and error
        reporting. Examples include audio libraries, content delivery networks,
        Firebase (for analytics and crash reporting), and RevenueCat (for
        subscription management). When you use the App, technical data and event
        information may be shared with these providers to operate and improve
        the service. These providers act under their own terms and privacy
        policies, and we require them by contract to handle data appropriately
        and only for allowed purposes.
      </p>

      <div className={s.title}>13) Analytics and Advertising</div>
      <p>
        We may use analytics tools (for example, Firebase Analytics) to
        understand how the App is used, measure feature adoption, and improve
        performance and user experience. Typical events can include screen
        views, sound selections, play/pause actions, timer usage, paywall
        views, and purchases. Where required by law, we will request consent or
        offer settings to limit analytics.
      </p>
      <p>
        In some versions, the free tier of the App may include advertising, such
        as a non-intrusive banner on the home screen. Advertising may be
        provided through third-party ad networks, which are subject to their own
        terms and policies. If you purchase or activate a premium plan that
        removes ads, the App will attempt to disable in-app advertising for
        your account or device. However, platform-level promotions or store
        banners not controlled by the App may still appear.
      </p>

      <div className={s.title}>14) No Guarantees of Outcomes</div>
      <p>
        While many people find white noise and nature sounds helpful for
        relaxation, baby soothing, or focus, we do not guarantee any particular
        outcome, such as falling asleep faster, improved sleep quality,
        reduction of stress, or calming of infants. Results vary between
        individuals and situations. The App does not replace professional
        medical, psychological, or parenting advice. You should consult
        appropriate professionals if you have concerns about sleep, stress,
        mental health, or infant care.
      </p>

      <div className={s.title}>15) Limitation of Liability</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and
        non-infringement. To the extent allowed by law, we are not liable for
        indirect, incidental, consequential, or punitive damages, loss of
        profits, data loss (including loss of settings, favorites, or cached
        audio), or business interruption arising from or related to your use of
        or inability to use the App. Some jurisdictions do not allow certain
        limitations or exclusions, so parts of this section may not apply to
        you.
      </p>

      <div className={s.title}>16) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your breach of these Terms, or your
        violation of any rights of a third party (including misuse of audio in
        violation of local rules or safe-sleep guidance).
      </p>

      <div className={s.title}>17) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>18) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        White Noice: Calm Sleep, which explains how information is collected,
        used, stored, and shared, including analytics and technical data. In
        case of conflict between these Terms and the Privacy Policy regarding
        data processing, the Privacy Policy will guide how we handle personal
        data, while these Terms govern your use of the App and its content. You
        should review the Privacy Policy regularly to understand current
        practices.
      </p>

      <div className={s.title}>19) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove sounds or features, change the free
        and premium offering, or modify these Terms from time to time. The “Last
        updated” date at the top of this document indicates when these Terms
        were last revised. Material changes may be highlighted in the App or via
        other reasonable means. By continuing to use the App after changes take
        effect, you accept the updated Terms. If you do not agree to the revised
        Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>20) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
